import styles from './PunchupQuantityCard.module.css';

import { PunchupStepper } from 'features/common/components/Punchup/primitives/forms/fields/PunchupStepper';
import { PoolRequestItem, poolClampedMax } from 'features/PunchupPages/SendTickets/poolBudget';
import { QuantityCardProps } from './types';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface RowQuantity {
	type?: string;
	quantity?: number;
}

export const PunchupQuantityCard = ({ recipientId, ticketTypes, poolBudget }: QuantityCardProps) => {
	const { t } = useTranslation();
	const { setValue, watch } = useFormContext();

	// Empty deps: initialise form slots once on mount only. Re-running on ticketTypes
	// changes would reset quantities the user has already entered.
	useEffect(() => {
		ticketTypes.forEach(({ type, ticketTypeId, initialPrice }, i) => {
			const name = `recipients[${recipientId}].ticketTypes[${i}]`;
			setValue(name, { type, quantity: 0, initialPrice, ticket_type_id: ticketTypeId });
		});
	}, []);

	// Subscribe to the whole row array so a sibling tier's change re-derives every pooled
	// tier's cap live (the shared budget is consumed across all member tiers, not per-tier).
	const rows: RowQuantity[] = watch(`recipients[${recipientId}].ticketTypes`) ?? [];
	// Tag each row with its resolved pool (by tier name) so the shared clamp can subtract same-pool draw
	// across tiers. Index-aligned with `ticketTypes` (the form rows are seeded in that order on mount).
	const poolRequests: PoolRequestItem[] = rows.map((row) => ({
		poolId: row.type !== undefined ? poolBudget?.poolIdByTypeName[row.type] : undefined,
		quantity: row.quantity ?? 0,
	}));

	return (
		<div className={styles.list}>
			{ticketTypes.map(({ type, sold, amount, soldOut, ticketTypeId }, i) => {
				const name = `recipients[${recipientId}].ticketTypes[${i}]`;
				const available = amount - sold;
				const isSoldOut = soldOut || available <= 0;
				// The tier's own inventory remains, but its shared pool has no sendable capacity at all
				// (`sellableRemaining ≤ 0`) — so it can't send regardless of this draft. This is the pool analog
				// of "Sold out". Distinct from the recoverable case where siblings have *drafted* the pool down
				// to 0 (poolRemaining still > 0): that stays a disabled stepper so the manager reduces a sibling.
				const poolId = poolBudget?.poolIdByTypeName[type];
				const pool = poolId !== undefined ? poolBudget?.pools.find((p) => p.poolId === poolId) : undefined;
				const isPoolFull = !isSoldOut && pool !== undefined && pool.sellableRemaining <= 0;
				const quantity = watch(`${name}.quantity`) ?? 0;
				// min(tier own-remaining, pool remaining − sibling same-pool draw) — the shared clamp (bulk uses
				// the same helper). Infinity-free: returns `available` unchanged for a non-pooled tier.
				const max = poolClampedMax(available, poolBudget?.poolIdByTypeName[type], poolRequests, i, poolBudget);

				return (
					<div className={styles.row} key={ticketTypeId}>
						<span className={styles.label}>{type}</span>
						{isSoldOut ? (
							<span className={styles.soldOut}>{t('sold-out', 'Sold out')}</span>
						) : isPoolFull ? (
							<span className={styles.soldOut}>{t('pool-full', 'Pool full')}</span>
						) : (
							<PunchupStepper value={quantity} onChange={(v) => setValue(`${name}.quantity`, v)} min={0} max={max} />
						)}
					</div>
				);
			})}
		</div>
	);
};
